import {Module} from "vuex";
import {IDBase, QueryFacade} from "@/controller/QueryFacade";
import {AsyncJavaRequest, Main} from "@/controller/main";
import {DieAndToolCodeRow, ImportConfigStates} from "@/store/ImportConfigStore";


interface ToolCreationSetup {
    tool: SimpleToolLine,
    component: SimpleComponentLine,
    position: number
}

export class ComponentImportState {
    public existing: number = 0;
    public existingStructure: {
        projects: SimpleProjectLine[],
        companies: SimpleCompanyLine[]
    } = {projects: [], companies: []};
    public csvToImport: CSVLine[] = [];
    public started: number = Date.now();
    public stateMessage: string = "";
    public selectedProject: SimpleProjectLine | undefined;
    public importConfigStates: ImportConfigStates = new ImportConfigStates();

    public requiredComponentCreation: { [partNumber: string]: RequiredComponentCreation } = {};
    public componentCreations: { [partNumber: string]: RequiredComponentCreation } = {};
    public requiredToolCreation: { [componentToolKey: string]: ToolCreationSetup
    } = {};

    public companyComponentTypeId?: number;
    public componentComponentTypeId?: number;

    public createdComponents: number = 0;
    createdTools: number = 0;
    updatedTools:number=0;
}

class CSVLine {
    doImport: boolean = false;
    canImport: boolean = false;
    exists: boolean = false;
    data: string[] = [];

    message?: string;
    mainPart?: SimpleComponentLine;
    company?: SimpleCompanyLine;
    companyPart?: SimpleComponentLine;
    sourceShortHand?: string;
    dieSetData?: { [dieSetNumber: string]: DieSetData };
    partName?: string;

}

interface RequiredComponentCreation {
    component?: SimpleComponentLine;
    callbacks: Array<(createdComponent: SimpleComponentLine) => void>;
}

interface DieSetData {
    dieAndToolCodeRow: DieAndToolCodeRow[];
    component?: SimpleComponentLine;
}

class SimpleProjectLine {
    name: string = "";
    children: SimpleComponentLine[] = [];

}

class SimpleCompanyLine {
    firmaId?: number;
    name?: string;
    strasse?: string;
    nummer?: string;
    plz?: string;
    ort?: string;
    land?: string;
    email?: string;
    vendorId?: string;
    shorthand?: string;
    contractPersonId?: number;
}

export class SimpleComponentLine {
    type: string = "ib";
    ib: string = "";
    componentId: number | undefined;
    projectId: number | undefined;
    parentId: number | undefined;
    supplierId: number | undefined;
    contractPersonId: number | undefined;
    contractCompanyId: number | undefined;
    throughputTime: number | undefined;
    componentTypeId: number | undefined;
    name: string | undefined;
    description: string | undefined;
    contractPersonFullName: string | undefined;
    partNumber: string | undefined;
    inventoryNumbers: string | undefined;
    supplierName: string | undefined;
    children: SimpleComponentLine[] = [];
    report:boolean = true;
    usedCurrency:string|undefined;
}

class SimpleToolLine {
    werkzeugId: number | undefined;
    nummer: string | undefined;
    name: string | undefined;
    beschreibung: string | undefined;
    bauteilId: number | undefined;
    laufendenummer: number | undefined;
    istaktiv: boolean = true;
}

export class ComponentImportStore implements Module<any, unknown> {
    public namespaced = true;
    public state: ComponentImportState = new ComponentImportState();
    public getters: { [func: string]: () => any } = {};
    public mutations = {
        setMessage(state: ComponentImportState, message: string) {
            state.stateMessage = message;
        },

        setCsv(state: ComponentImportState, csv: string) {
            componentImportStore.currentCsv = csv;
            state.csvToImport = [];
            state.started = Date.now();
            const data = csv2array(csv, ";");
            for (let datum of data) {
                let csvLine = new CSVLine();
                csvLine.data = datum;
                componentImportStore.analyzeImportData(csvLine);

                if (csvLine.canImport) {
                    csvLine.doImport = true;
                }

                state.csvToImport.push(csvLine);
            }

        },

        setProject(state: ComponentImportState, project: SimpleProjectLine) {
            state.selectedProject = project;
        },

        async importSelected(state: ComponentImportState) {
            const keysToDelete: string[] = [];
            let rerun = false;
            for (let key in state.requiredComponentCreation) {
                const create = state.requiredComponentCreation[key];
                keysToDelete.push(key);
                if (!create.component) {
                    rerun = true;
                    continue;
                }
                const data = await Main.postMessage(new AsyncJavaRequest("call", "saveComponent",
                    JSON.stringify(create.component)));
                state.createdComponents++;


                for (let callback of create.callbacks) {
                    callback(data);
                }

            }

            for (let key of keysToDelete) {
                state.componentCreations[key] = state.requiredComponentCreation[key];
                delete state.requiredComponentCreation[key];
            }


            if (rerun) {
                console.warn("rerunning import");
                state.requiredComponentCreation = {};
                state.requiredToolCreation = {};

                for (let csvLine of state.csvToImport) {
                    componentImportStore.analyzeImportData(csvLine);
                }

                await componentImportStore.mutations.importSelected(state);

            } else {
                for (let csvLine of state.csvToImport) {
                    if(csvLine.dieSetData){
                        let imp = true;
                        // for (let dieSetDataKey in csvLine.dieSetData) {
                        //     if(state.componentCreations[dieSetDataKey]){
                        //         imp = true;
                        //         break;
                        //     }
                        // }
                        if(imp) {
                            await ComponentImportStore.registerToolCreationForComponent(csvLine);
                        }
                    }
                }

                //sort it
                const toCreate:ToolCreationSetup[] = [];
                for (let tceKey in state.requiredToolCreation) {
                    toCreate.push(state.requiredToolCreation[tceKey]);
                }

                toCreate.sort((a,b)=>{
                    return b.position-a.position;
                });

                for (let tce of toCreate) {
                    const existingTools:SimpleToolLine[] = await Main.postMessage(new AsyncJavaRequest("call", "allComponentTools",
                        ""+tce.component.componentId));

                    let update=false;
                    let toolToStore:SimpleToolLine = tce.tool;
                    for (let existingTool of existingTools) {
                        if(existingTool.beschreibung === tce.tool.beschreibung){
                            existingTool.laufendenummer = tce.tool.laufendenummer;
                            toolToStore = existingTool;
                            update=true;
                        }
                    }

                    //lazy id assignment since it could be created after analysis
                    if(!update) {
                        tce.tool.bauteilId = tce.component.componentId;
                    }

                    await Main.postMessage(new AsyncJavaRequest("call", "createTool",
                        JSON.stringify(toolToStore)));
                    if(!update) {
                        state.createdTools++;
                    }
                    else {
                        state.updatedTools++;
                    }
                }
                state.requiredToolCreation = {};

                alert("Import finished, \n" + state.createdComponents + " components created\n " + state.createdTools + "/"+state.csvToImport.length+" tools created\n"+state.updatedTools+" updated tools");
            }
        },

        async loadFromDb(state: ComponentImportState) {
            const data = await Main.postMessage(new AsyncJavaRequest("call", "allComponents"));
            state.existingStructure = data;
            if (state.existingStructure.projects.length > 0) {
                state.selectedProject = (<any>state.existingStructure.projects)[0];
            }

            const orgstate = new ImportConfigStates();
            for (let key of Object.keys(orgstate)) {
                const res = await <IDBase><any>QueryFacade.get("import_config", key);
                (<any>state).importConfigStates[key] = (<any>orgstate)[key];
                if (res) {
                    (<any>state).importConfigStates[res.id] = res;
                }
            }
        }
    };
    private currentCsv: string | undefined;

    constructor() {
        for (let key of Object.keys(this.state)) {
            this.getters[key] = () => {
                return (<any>this.state)[key];
            }
        }
    }

    public static async registerToolCreationForComponent(csvLine: CSVLine) {
        for (let dieSetNumber in csvLine.dieSetData) {
            const dieSetDatum = csvLine.dieSetData[dieSetNumber];
            let existingTools: { [dieToolName: string]: SimpleToolLine } = {};
            if (dieSetDatum.component?.componentId) {
                const data: SimpleToolLine[] = await Main.postMessage(new AsyncJavaRequest("call", "allComponentTools",
                    "" + dieSetDatum.component.componentId));
                for (let datum of data) {
                    if (datum.beschreibung) {
                        existingTools[datum.beschreibung] = datum;
                    }
                }
            }
            let position = 0;
            for (let codeRowElement of dieSetDatum.dieAndToolCodeRow) {

                if (existingTools[codeRowElement.dieToolName]) {
                    continue;
                }

                const tool: SimpleToolLine = new SimpleToolLine();
                tool.beschreibung = codeRowElement.dieToolName + "_" + codeRowElement.id;
                if (dieSetDatum.component) {
                    const creationHash = tool.beschreibung + "--" + dieSetDatum.component.partNumber;
                    if(componentImportStore.state.requiredToolCreation[creationHash]){
                        continue;
                    }

                    tool.laufendenummer = (position+1)*10;
                    console.log(tool.laufendenummer )
                    componentImportStore.state.requiredToolCreation[creationHash] = {
                        component: dieSetDatum.component,
                        tool,
                        position: position++
                    };
                }
            }

        }
    }

    private analyzeImportData(csvLine: CSVLine): void {
        const data = csvLine.data;
        csvLine.exists = false;
        csvLine.canImport = data[0].trim() != "" &&
            !data[0].startsWith("Dieset Timing") &&
            !data[0].startsWith("Master program") &&
            !csvLine.exists;

        if (!csvLine.canImport) {
            return;
        }

        const companies = this.state.existingStructure.companies;

        for (let company of companies) {
            if (company.shorthand == data[3]) {
                csvLine.company = company;
                break;
            }
        }

        if (!csvLine.company) {
            csvLine.canImport = false;
            csvLine.message = "no company found for Construction Source '" + data[3] + "', check your companies";
            return;
        }

        const prodSource = data[4];
        for (let productionSourceRow of this.state.importConfigStates.ProductionSourceMap.map) {
            if (productionSourceRow.stampsImport == prodSource) {
                csvLine.sourceShortHand = productionSourceRow.shorthand;
            }
        }

        const toolIdsToAdd = data[5].split(",");
        const toolsToAdd: DieAndToolCodeRow[] = [];
        for (let toolid of toolIdsToAdd) {
            for (let toolRow of this.state.importConfigStates.DieAndToolCodeMap.map) {
                if (toolRow.id == toolid) {
                    toolsToAdd.push(toolRow);
                }
            }
        }

        csvLine.partName = data[6] + "-" + data[7];
        const relatedIds: string[] = data[8].split(",");
        const relatedNames: string[] = data[9].split(",");

        if (relatedIds && relatedNames) {
            if (relatedIds.length != relatedNames.length) {
                csvLine.canImport = false;
                csvLine.message = "Amount of Related Part No's != Related Part Names, check your CSV";
                return;
            }

            for (let i = 0; i < relatedIds.length; i++) {
                if (relatedIds[i] && relatedNames[i]) {
                    csvLine.partName += "\n";
                    csvLine.partName += relatedIds[i] + "-" + relatedNames[i];
                }
            }
        }

        if (!csvLine.sourceShortHand) {
            csvLine.canImport = false;
            csvLine.message = "no shortHand found for Production Source '" + prodSource + "', check your Import Config";
            return;
        }

        const dieSetPrefix = data[2] + "_" + csvLine.sourceShortHand;
        for (let dieAndToolCodeRow of toolsToAdd) {
            const name = dieSetPrefix + "_" + dieAndToolCodeRow.dieToolPostfix;
            if (!csvLine.dieSetData) {
                csvLine.dieSetData = {};
            }
            if (!csvLine.dieSetData[name]) {
                csvLine.dieSetData[name] = {
                    dieAndToolCodeRow: []
                };
            }

            csvLine.dieSetData[name].dieAndToolCodeRow.push(dieAndToolCodeRow);
        }

        //check which components have to be created
        let children = this.state.selectedProject?.children;
        if (children) {
            //search for Ford Project node
            for (let child of children) {
                if (child.partNumber?.startsWith(data[0])) {
                    csvLine.mainPart = child;
                    break;
                }
            }

            //search for Company node
            if (csvLine.mainPart) {
                children = csvLine.mainPart?.children;
                if (children && csvLine.company) {
                    for (let child of children) {
                        if (!this.state.companyComponentTypeId) {
                            this.state.companyComponentTypeId = child.componentTypeId;
                        }
                        if (
                            child.contractCompanyId == csvLine.company.firmaId
                        ) {
                            csvLine.companyPart = child;
                            break;
                        }
                    }
                }
            } else {
                csvLine.canImport = false;
                csvLine.message = "Main component for " + data[0] + " must be created manually";
                return;
            }

            //search for components by number
            if (csvLine.companyPart) {
                children = csvLine.companyPart?.children;
                if (children && csvLine.dieSetData) {
                    for (let child of children) {
                        if (!this.state.componentComponentTypeId) {
                            this.state.componentComponentTypeId = child.componentTypeId;
                        }
                        for (let dieSetNumber in csvLine.dieSetData) {
                            const dieSetDatum = csvLine.dieSetData[dieSetNumber];
                            if (dieSetNumber == child.partNumber) {
                                dieSetDatum.component = child;
                                break;
                            }
                        }
                    }
                }
            } else {
                const company = csvLine.company;
                if (typeof company.name == "string") {
                    const companyKey = csvLine.mainPart.componentId+'-'+company.name;

                    if (!this.state.requiredComponentCreation[companyKey]) {
                        const companyComponent = this.createComponentInstanceForCompany(company, csvLine, csvLine.mainPart, 1);
                        this.state.requiredComponentCreation[companyKey] = {
                            component: companyComponent,
                            callbacks: []
                        };
                    }
                    this.state.requiredComponentCreation[companyKey].callbacks.push((created: SimpleComponentLine) => {
                        csvLine.companyPart = created;
                        this.createComponentsForCompany(csvLine);
                    });
                }
            }
            this.createComponentsForCompany(csvLine);


        } else {
            console.error("no children in selected project to iterate", this.state);
        }


        console.info(csvLine, this.state);
        return;
    }

    private createComponentsForCompany(csvLine: CSVLine) {
        for (let dieSetNumber in csvLine.dieSetData) {
            if (!csvLine.dieSetData[dieSetNumber].component) {
                this.registerRequiredComponentCreation(dieSetNumber, csvLine);
                if (!this.state.requiredComponentCreation[dieSetNumber].callbacks) {
                    this.state.requiredComponentCreation[dieSetNumber].callbacks = [];
                }
                this.state.requiredComponentCreation[dieSetNumber].callbacks.push((created: SimpleComponentLine) => {
                    if (csvLine.dieSetData) {
                        csvLine.dieSetData[dieSetNumber].component = created;
                    }
                });
            }
        }
    }

    private registerRequiredComponentCreation(dieSetNumber: string, csvLine: CSVLine) {

        if (!this.state.requiredComponentCreation[dieSetNumber]) {
            if (csvLine.companyPart) {
                this.state.requiredComponentCreation[dieSetNumber] = {
                    component: this.createComponentInstance(dieSetNumber, csvLine, csvLine.companyPart, 0),
                    callbacks: []
                };
            } else {
                this.state.requiredComponentCreation[dieSetNumber] = {
                    callbacks: []
                };
            }
        }
    }

    private createComponentInstanceForCompany(c: SimpleCompanyLine, csvLine: CSVLine, parent: SimpleComponentLine, typeId: number | undefined) :SimpleComponentLine {
        for (let child of parent.children) {
            if(child.partNumber === c.name){
                //exists already
                return child;
            }
        }

        const newComponent = new SimpleComponentLine();
        newComponent.partNumber = c.name;
        newComponent.name = c.name;
        newComponent.contractCompanyId = c.firmaId;
        newComponent.supplierId = c.firmaId;
        newComponent.contractPersonId = c.contractPersonId;
        newComponent.parentId = parent.componentId;
        newComponent.projectId = parent.projectId;
        newComponent.componentTypeId = typeId;
        newComponent.report=true;
        delete newComponent.ib;
        delete newComponent.type;
        return newComponent;
    }

    private createComponentInstance(partnumber: string, csvLine: CSVLine, parent: SimpleComponentLine, typeId: number | undefined) {
        const newComponent = new SimpleComponentLine();
        newComponent.partNumber = partnumber;
        newComponent.name = csvLine.partName;
        newComponent.contractCompanyId = csvLine.company?.firmaId;
        newComponent.contractPersonId = parent.contractPersonId;
        newComponent.supplierId = csvLine.company?.firmaId;
        newComponent.parentId = parent.componentId;
        newComponent.projectId = parent.projectId;
        newComponent.componentTypeId = typeId;
        delete newComponent.ib;
        delete newComponent.type;
        return newComponent;
    }
}


/**
 * Convert data in CSV (comma separated value) format to a javascript array.
 *
 * Values are separated by a comma, or by a custom one character delimeter.
 * Rows are separated by a new-line character.
 *
 * Leading and trailing spaces and tabs are ignored.
 * Values may optionally be enclosed by double quotes.
 * Values containing a special character (comma's, double-quotes, or new-lines)
 *   must be enclosed by double-quotes.
 * Embedded double-quotes must be represented by a pair of consecutive
 * double-quotes.
 *
 * Example usage:
 *   var csv = '"x", "y", "z"\n12.3, 2.3, 8.7\n4.5, 1.2, -5.6\n';
 *   var array = csv2array(csv);
 *
 * Author: Jos de Jong, 2010
 *
 * @param {string} data      The data in CSV format.
 * @param {string} delimeter [optional] a custom delimeter. Comma ',' by default
 *                           The Delimeter must be a single character.
 * @return {Array} array     A two dimensional array containing the data
 * @throw {String} error     The method throws an error when there is an
 *                           error in the provided data.
 */
function csv2array(data: string, delimeter: string): Array<Array<string>> {
    // Retrieve the delimeter
    if (delimeter == undefined)
        delimeter = ',';
    if (delimeter && delimeter.length > 1)
        delimeter = ',';

    // initialize variables
    var newline = '\n';
    var eof = '';
    var i = 0;
    var c = data.charAt(i);
    var row = 0;
    var col = 0;
    var array: Array<Array<string>> = [];

    while (c != eof) {
        // skip whitespaces
        while (c == ' ' || c == '\t' || c == '\r') {
            c = data.charAt(++i); // read next char
        }

        // get value
        let value: string = "";
        if (c == "\"") {
            // value enclosed by double-quotes
            c = data.charAt(++i);

            do {
                if (c != "\"") {
                    // read a regular character and go to the next character
                    value += c;
                    c = data.charAt(++i);
                }

                if (c == "\"") {
                    // check for escaped double-quote
                    var cnext = data.charAt(i + 1);
                    if (cnext == "\"") {
                        // this is an escaped double-quote.
                        // Add a double-quote to the value, and move two characters ahead.
                        value += "\"";
                        i += 2;
                        c = data.charAt(i);
                    }
                }
            }
            while (c != eof && c != "\"");

            if (c == eof) {
                throw "Unexpected end of data, double-quote expected";
            }

            c = data.charAt(++i);
        } else {
            // value without quotes
            while (c != eof && c != delimeter && c != newline) {
                value += c;
                c = data.charAt(++i);
            }
        }

        // add the value to the array
        if (array.length <= row)
            array.push([]);
        array[row].push(value);

        // skip whitespaces
        while (c == ' ' || c == '\t' || c == '\r') {
            c = data.charAt(++i);
        }

        // go to the next row or column
        if (c == delimeter) {
            // to the next column
            col++;
        } else if (c == newline) {
            // to the next row
            col = 0;
            row++;
        } else if (c != eof) {
            // unexpected character
            let nearby = "";
            for (let j = 0; j < 100 && (data.length - 1) > (i + j); j++) {
                nearby += data.charAt(i + j);
            }

            throw "Delimiter expected after character " + i + " found " + c + " nearby " + nearby;
        }

        // go to the next character
        c = data.charAt(++i);
    }

    return array;
}

export const componentImportStore = new ComponentImportStore();